import { useTranslation } from 'react-i18next';
import { ArrowUp, ImagePlus, MessageCircle, Sparkles, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ChatSettings } from '../../../modules/vendor/settings/models/config.model';
import { readableOn, resolveLauncherColors, returningNudgeText } from '../../../modules/vendor/settings/models/chat-settings';

/** `returning`: the product page as a returning shopper sees it, the one bubble in place of the questions. */
export type PreviewView = 'chat' | 'page' | 'returning';

/**
 * A still mock-up of what shoppers see, redrawn from the settings as they are
 * edited.
 *
 * Deliberately not the real panel in an iframe: that would call the shopper
 * endpoints on every keystroke, need a product SKU and a visitor token, and
 * show a conversation from the vendor's own browser history. What a vendor
 * needs here is the look — colours, wording, corner — and that can be drawn
 * from the settings alone.
 */
export const ChatPreview = ({
    settings,
    brandColor,
    brandSecondaryColor,
    company,
    view,
    hovered,
}: {
    settings: ChatSettings;
    brandColor?: string;
    brandSecondaryColor?: string;
    company?: string;
    view: PreviewView;
    hovered: boolean;
}) => {
    const { t } = useTranslation();
    const colors = resolveLauncherColors(settings, brandColor, brandSecondaryColor);
    const left = settings.position === 'left';
    const label = settings.label || t('CHAT.APPEARANCE.LABEL_PLACEHOLDER', 'Shopping Assistant');
    const questions = [
        t('CHAT.PREVIEW.QUESTION_1', 'Is this suitable for a bathroom floor?'),
        t('CHAT.PREVIEW.QUESTION_2', 'What grout colour works best with it?'),
    ];

    const nudge = returningNudgeText(
        settings.returningNudge?.message,
        t('SHOPPER_UI.SETTINGS.RETURNING_MESSAGE_PLACEHOLDER'),
        t('CHAT.PREVIEW.PRODUCT', 'Your product'),
    );

    const launcher = (
        <Launcher
            label={label}
            questions={view === 'returning' ? [nudge] : questions}
            emphasis={view === 'returning'}
            background={hovered ? colors.hoverBackground : colors.background}
            text={hovered ? colors.hoverText : colors.text}
            left={left}
            withQuestions={view !== 'chat' || hovered}
        />
    );

    return (
        /*
         * Shrink-wrapped around the chat window, so the window and its launcher
         * line up on one edge while the pair sits in the middle of the canvas.
         * Full width only for the product page, which is drawn wide.
         */
        <div
            className={cn(
                'flex flex-col gap-6',
                view === 'chat' ? 'w-fit max-w-full' : 'w-full max-w-[860px]',
                left ? 'items-start' : 'items-end',
            )}
        >
            {view === 'chat' ? (
                <ChatWindow
                    settings={settings}
                    accent={brandColor || colors.background}
                    company={company}
                    questions={questions}
                />
            ) : (
                <ProductPage settings={settings} background={colors.background} questions={questions} />
            )}
            {launcher}
        </div>
    );
};

/** The floating dock: questions stacked over the labelled launcher. */
const Launcher = ({
    label,
    questions,
    background,
    text,
    left,
    withQuestions,
    emphasis = false,
}: {
    label: string;
    questions: string[];
    background: string;
    text: string;
    left: boolean;
    withQuestions: boolean;
    /** The returning-visitor bubble: one line, set a little heavier than the questions. */
    emphasis?: boolean;
}) => (
    <div className={cn('flex flex-col gap-2', left ? 'items-start' : 'items-end')}>
        {withQuestions && questions.map((q) => (
            <span
                key={q}
                className={cn(
                    'max-w-[300px] rounded-2xl border border-black/80 px-4 py-2 text-sm shadow-md transition-colors',
                    left ? 'text-left' : 'text-right',
                    emphasis && 'py-3 font-semibold',
                )}
                data-testid={emphasis ? 'preview-returning-nudge' : undefined}
                style={{ backgroundColor: background, color: text }}
            >
                {q}
            </span>
        ))}
        <div className="relative inline-flex">
            <span
                className="inline-flex h-14 items-center gap-2 rounded-full pl-4 pr-5 text-sm font-semibold shadow-lg transition-colors"
                style={{ backgroundColor: background, color: text }}
            >
                <MessageCircle className="size-7" strokeWidth={1.7} />
                {label}
            </span>
            <span
                className={cn(
                    'absolute -top-1.5 flex size-5 items-center justify-center rounded-full border-2 border-white bg-gray-400 text-white',
                    left ? '-left-1.5' : '-right-1.5',
                )}
            >
                <X className="size-3" strokeWidth={3} />
            </span>
        </div>
    </div>
);

/** The conversation panel, as it opens on a product page. */
const ChatWindow = ({
    settings,
    accent,
    company,
    questions,
}: {
    settings: ChatSettings;
    accent: string;
    company?: string;
    questions: string[];
}) => {
    const { t } = useTranslation();
    const greeting = settings.greeting
        || t('CHAT.CONVERSATION.GREETING_PLACEHOLDER', 'Hi — ask me anything about this product, or tell me what you’re looking for.');

    return (
        <div className="flex h-[520px] w-[380px] max-w-full flex-col overflow-hidden rounded-2xl border bg-white text-[#1a1a2e] shadow-xl">
            <div className="border-b px-4 py-3 text-center text-sm font-medium">
                {t('CHAT.PREVIEW.ASSISTANT', 'Assistant')} {company || ''}
            </div>

            <div className="mx-3 mt-3 flex items-center gap-3 rounded-xl border p-2">
                <div className="size-10 shrink-0 rounded-lg bg-gradient-to-br from-stone-200 to-stone-300" />
                <span className="text-sm font-medium">{t('CHAT.PREVIEW.PRODUCT', 'Your product')}</span>
            </div>

            {settings.roomPhoto?.enabled && (
                <div className="mx-3 mt-2 flex items-center justify-center gap-2 rounded-full border py-2 text-xs font-medium">
                    <ImagePlus className="size-4" />
                    {t('CHAT.PREVIEW.SEE_IN_ROOM', 'See it in my room')}
                </div>
            )}

            <div className="flex-1 overflow-hidden px-4 py-4 text-sm leading-relaxed">{greeting}</div>

            <div className="flex flex-col items-start gap-2 px-4 pb-2">
                {questions.map((q) => (
                    <span key={q} className="rounded-full border px-3 py-1.5 text-xs">{q}</span>
                ))}
            </div>

            <div className="flex items-center justify-center gap-1 pb-1 text-[10px] text-gray-400">
                <Sparkles className="size-3" />
                {t('CHAT.PREVIEW.AI_NOTICE', 'You’re chatting with an AI')}
            </div>

            <div className="border-t p-3">
                <div className="flex items-center gap-2 rounded-full border py-1 pl-3 pr-1">
                    {settings.roomPhoto?.enabled && <ImagePlus className="size-5 text-gray-500" />}
                    <span className="flex-1 text-sm text-gray-400">{t('CHAT.PREVIEW.PLACEHOLDER', 'Ask a question…')}</span>
                    <span
                        className="flex size-8 items-center justify-center rounded-full"
                        style={{ backgroundColor: accent, color: readableOn(accent) }}
                    >
                        <ArrowUp className="size-4" />
                    </span>
                </div>
            </div>
        </div>
    );
};

/** A product page with the questions block where the vendor placed it. */
const ProductPage = ({
    settings,
    background,
    questions,
}: {
    settings: ChatSettings;
    background: string;
    questions: string[];
}) => {
    const { t } = useTranslation();
    const prompts = settings.prompts;
    const blockColor = prompts?.color || background;
    const blockText = readableOn(blockColor);

    return (
        <div className="grid w-full grid-cols-1 gap-6 rounded-2xl border bg-white p-6 text-[#1a1a2e] shadow-xl sm:grid-cols-2">
            <div className="aspect-square rounded-xl bg-gradient-to-br from-stone-200 to-stone-300" />
            <div className="flex flex-col gap-3">
                <div className="h-6 w-3/4 rounded bg-stone-200" />
                <div className="h-3 w-1/3 rounded bg-stone-100" />
                <div className="h-3 w-full rounded bg-stone-100" />
                <div className="h-3 w-5/6 rounded bg-stone-100" />
                <div className="mt-2 h-10 w-full rounded-lg bg-[#1a1a2e]" />

                {prompts?.enabled ? (
                    <div className="mt-3 flex flex-col gap-2">
                        <span className="text-base font-bold">
                            {prompts.heading || t('CHAT.PROMPTS.HEADING_PLACEHOLDER', 'A question about this product?')}
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {[...questions, prompts.ctaLabel || t('CHAT.PROMPTS.CTA_PLACEHOLDER', 'Ask my question')].map((q, i) => (
                                <span
                                    key={q}
                                    className={cn('rounded-xl px-3 py-1.5 text-xs', i === questions.length && 'font-bold')}
                                    style={{ backgroundColor: blockColor, color: blockText }}
                                >
                                    {q}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="mt-3 text-xs text-gray-400">
                        {t('CHAT.PREVIEW.PROMPTS_OFF', 'The questions block is off.')}
                    </p>
                )}
            </div>
        </div>
    );
};
